/// <amd-module name="Environments/EnvironmentTraceabilityWorkItems/Components/WorkItemsComponent" />
import React = require("react");
import ReactDOM = require("react-dom");

import {
  EnvironmentBaseComponent,
  IEnvironmentBaseComponentState
} from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";

import { WorkItemRef } from "Environments/Client/EnvironmentContracts";
import { WorkitemsActionsCreator } from "Environments/EnvironmentTraceabilityWorkItems/ActionsCreators/WorkitemsActionsCreator";
import * as Constants from "Environments/EnvironmentTraceabilityWorkItems/Common/Constants";
import * as Resources from "Environments/EnvironmentTraceabilityWorkItems/Resources";
import { WorkitemsStore } from "Environments/EnvironmentTraceabilityWorkItems/Stores/WorkitemsStore";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";
import { WorkItemType, WorkItemTypeColorAndIcon } from "Environments/RemoteClients/Contracts/WorkItemTracking";

import { ObservableValue } from "VSS/Core/Observable";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { TypeIconSize, WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";

import { publishTelemetry } from "Environments/EnvironmentsCommon/Telemetry";
import { Action, Area, FeatureNames } from "Environments/EnvironmentTraceabilityWorkItems/Common/Utils";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { ITableColumn, ITableRow, SimpleTableCell, Table } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { VssPersona } from 'VSSUI/VssPersona';

interface IWorkitemsTableItem extends WorkItemRef {
}

export interface IWorkItemsProperties extends IVssComponentProperties {
  instanceId: string;
  environmentId: number;
  environmentExecutionRecordId: number;
  ownerId: number;
}

export interface IState extends IEnvironmentBaseComponentState {
  itemProvider: ArrayItemProvider<IWorkitemsTableItem>;
  icons: WorkItemType[];
}

class WorkItemsComponent extends EnvironmentBaseComponent<IWorkItemsProperties, IState> {

  public getInstanceId(context: IVssComponentContext): string {
    return "wokitems-instance-id-" + this.componentId;
  }

  public constructor( props: IWorkItemsProperties, context: IVssComponentContext ) {
    super(props, context);
    this._actionCreator = ActionCreatorManager.CreateActionCreator<WorkitemsActionsCreator>(WorkitemsActionsCreator, this._instanceId);
    this._store = StoreManager.CreateStore<WorkitemsStore>(WorkitemsStore, this._instanceId);
    this._actionCreator.getWorkitems(this.context.pageContext);
    publishTelemetry(context.pageContext, Area, FeatureNames.EnvironmentRunDetailsView, { "Action": Action.WorkitemsTabNavigation, "EnvironmentId": props.environmentId.toString(), "EnvironmentExecutionRecordId": props.environmentExecutionRecordId.toString(), "OwnerId": props.ownerId.toString()});

    this.state = {
        itemProvider: this._getItemProvider(),
        icons: [],
        errorMessages: {}
    }

    this._store.addChangedListener(this._onStoreChanged);

    this._actionCreator.getIcons(this.context.pageContext);
  }

  public render(): JSX.Element {
    const workitemsProvider = this.state.itemProvider;

    return (
      <div className="page-content page-content-top">
        <Card className="bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
          <div>
            <Table<IWorkitemsTableItem>
              ariaLabel={Resources.WorkitemColumnName}
              key={workitemsProvider.length}
              columns={this._sizableColumns}
              itemProvider={workitemsProvider}
              onActivate={this._onRowClicked}
            />
          </div>
        </Card>
      </div>
    );
  }

  private _onStoreChanged = (): void => {
    const icons = this._store.getIcons();
    
    this.setState({
        itemProvider: this._getItemProvider(),
        icons: icons
    });
  };

  private _getItemProvider(): ArrayItemProvider<IWorkitemsTableItem> {
    const workitems = this._store.getWorkitems() || [];

    return new ArrayItemProvider<IWorkitemsTableItem>(workitems.map<IWorkitemsTableItem>(workitem => {
        return {
          id: workitem.id,
          type: workitem.type,
          currentState: workitem.currentState,
          title: workitem.title,
          assignedTo: workitem.assignedTo,
          createdOn: workitem.createdOn,
          url: workitem.url || (workitem.links && workitem.links.web && workitem.links.web.href)
        } as IWorkitemsTableItem;
      })
    );
  }

  private _renderWorkitemColumn = ( rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IWorkitemsTableItem>, tableItem: IWorkitemsTableItem ): JSX.Element => {
    return (
      <SimpleTableCell
        key={"workitems-col-" + columnIndex}
        ariaLabel={"workitems-col-" + columnIndex}
        columnIndex={columnIndex}
        tableColumn={tableColumn}
        children={
          <>
            {this._getWorkitemIcon(tableItem.type)}
            <div className="flex-column scroll-hidden">
              <Tooltip overflowOnly={true} text={tableItem.title}>
                <div className="font-weight-semibold text-ellipsis">{tableItem.title}</div>
              </Tooltip>
              <div className="rundetails-secondary-text secondary-text fontSizeS">
                {tableItem.type} {tableItem.id}
              </div>
            </div>
          </>
        }
      />
    );
  }

  private _getWorkitemIcon = (type: string) : JSX.Element => {
    let iconEl: JSX.Element = <span />;
    for(let workItemTypeData of this.state.icons) {
      if(workItemTypeData.name === type) {
        const icon: WorkItemTypeColorAndIcon = {
          color: workItemTypeData.color,
          icon: workItemTypeData.icon && workItemTypeData.icon.id,
          workItemTypeName: type,
          isDisabled: workItemTypeData.isDisabled
        }
        iconEl = <WorkItemTypeIcon data={icon} size={TypeIconSize.large} className="icon-large-margin" />;
        break;
      }
    }
    return iconEl;
  }

  private _renderStateColumn = ( rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IWorkitemsTableItem>, tableItem: IWorkitemsTableItem ): JSX.Element => {
    return (
      <SimpleTableCell 
        key={"commits-col-" + columnIndex} 
        columnIndex={columnIndex} 
        tableColumn={tableColumn}
        ariaLabel={"commits-col-" + columnIndex}
      >
        <Tooltip text={tableItem.currentState}>
          <span className="flex-row">
            <Status
              {...this._getStatusProps(tableItem)}
              ariaLabel={tableItem.currentState}
              className="icon-large-margin"
              size={StatusSize.m}
            />
          </span>
        </Tooltip>
        <div className="flex-row scroll-hidden">
          <Tooltip overflowOnly={true} text={tableItem.currentState}>
            <span className={"text-ellipsis"}>{tableItem.currentState}</span>
          </Tooltip>
        </div>
      </SimpleTableCell>
    );
  };

  private _renderAssignedToColumn = ( rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IWorkitemsTableItem>, tableItem: IWorkitemsTableItem ): JSX.Element => {
    let assignedTo: IdentityRef = tableItem.assignedTo;
    if(!assignedTo) {
      assignedTo = {
          id: "",
          uniqueName: "",
          displayName: Resources.Unassigned_Identity,
          imageUrl: getAssetLocation(this.context.pageContext, Constants.Identity_UnassignedUserIcon)
      } as IdentityRef;
    }
    return (
      <SimpleTableCell 
        key={"commits-col-" + columnIndex} 
        columnIndex={columnIndex} 
        tableColumn={tableColumn}
        ariaLabel={"commits-col-" + columnIndex} 
        >
          <VssPersona
              size={"small"}
              className={"icon-large-margin"}
              identityDetailsProvider={{
                  getIdentityImageUrl: (size: number): string => {
                    return assignedTo.imageUrl;
                  },
                  getDisplayName: (): string => {
                    return assignedTo.displayName;
                  }
              }}
          />
          <div className="flex-row scroll-hidden">
            <Tooltip overflowOnly={true} text={assignedTo.displayName}>
              <span className={"text-ellipsis"}>{assignedTo.displayName}</span>
            </Tooltip>
          </div>
      </SimpleTableCell>
    );
  };

  private _renderDateColumn = ( rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IWorkitemsTableItem>, tableItem: IWorkitemsTableItem ): JSX.Element => {
    return (
      <SimpleTableCell
        key={"commits-col-" + columnIndex}
        columnIndex={columnIndex}
        tableColumn={tableColumn}
        ariaLabel={"commits-col-" + columnIndex}
        children={
          <div className={"secondary-text"}>
            <Ago format={AgoFormat.Compact} date={tableItem.createdOn} />
          </div>
        }
      />
    );
  };

  private _getStatusProps(item: WorkItemRef): IStatusProps {
    let currentState:string = item.currentState;
    for(let workItemTypeData of this.state.icons) {
      if(workItemTypeData.name === item.type) {
        for(let state of workItemTypeData.states) {
          if(state.name == item.currentState) {
            currentState = state.category;
            break;
          }
        }
        break;
      }
    }

    switch (currentState) {
      case "Proposed":
        return Statuses.Queued;
      case "InProgress":
        return Statuses.Running;
      case "Open":
        return Statuses.Running;
      case "Resolved":
        return Statuses.Success;
      case "Completed":
        return Statuses.Success;
      case "Closed":
        return Statuses.Success;
      case "Removed":
        return Statuses.Canceled;
      default:
        return Statuses.Queued;
    }
  }

  private _onSizeSizable = ( event: MouseEvent, index: number, width: number ): void => {
      (this._sizableColumns[index].width as ObservableValue<number>).value = width;
  };

  private _onRowClicked = ( event: any, row: ITableRow<IWorkitemsTableItem> ): void => {
    onClickFPS(this.context.pageContext, row.data.url, true);
  };

  private _sizableColumns = [
    {
      id: "workitem",
      name: Resources.WorkitemColumnName,
      minWidth: 400,
      width: new ObservableValue(500),
      renderCell: this._renderWorkitemColumn,
      onSize: this._onSizeSizable
    },
    {
      id: "state",
      name: Resources.StateColumnName,
      minWidth: 100,
      width: new ObservableValue(150),
      renderCell: this._renderStateColumn,
      onSize: this._onSizeSizable
    },
    {
      id: "assignedTo",
      name: Resources.AssignedToColumnName,
      minWidth: 150,
      width: new ObservableValue(200),
      renderCell: this._renderAssignedToColumn,
      onSize: this._onSizeSizable
    },
    {
      id: "date",
      name: Resources.DateColumnName,
      minWidth: 100,
      width: new ObservableValue(-100),
      renderCell: this._renderDateColumn,
      onSize: this._onSizeSizable
    }
  ];

  public componentDidMount(): void {
    this._actionCreator.getWorkitemsAsync(this.context.pageContext);
    super.componentDidMount();
  }

  public componentWillUnmount(): void {
      this._store.removeChangedListener(this._onStoreChanged);
      StoreManager.DeleteStore<WorkitemsStore>(WorkitemsStore, this._instanceId);
      ActionCreatorManager.DeleteActionCreator<WorkitemsActionsCreator>(WorkitemsActionsCreator, this._instanceId);
      super.componentWillUnmount();
  }

  private _store: WorkitemsStore;
  private _actionCreator: WorkitemsActionsCreator;

}

Components.add("workitemsView", WorkItemsComponent);
